/**
 * Race Data Layer
 * Handles fetching, transforming, and managing race position data
 * Supports both binary (.wtr) and JSON formats
 */

import { fetchBinaryRaceData } from './binaryDecoder.js';
import { buildApiUrl } from '../config.js';

const BINARY_URL = '/race-data.wtr';
const API_URL = buildApiUrl();
const POLL_INTERVAL = 60000; // 60 seconds

// ZeroSixZero color palette
const TEAM_COLORS = [
  '#0e4d68', '#03b3fd', '#EFA00B', '#e6332a', '#3a9d7c',
  '#22c55e', '#06b6d4', '#eab308', '#ef4444', '#8b5cf6',
  '#14b8a6', '#0ea5e9', '#f59e0b', '#f97316', '#d946ef',
  '#10b981', '#3b82f6', '#fbbf24', '#ec4899', '#6366f1',
  '#34d399', '#60a5fa', '#fcd34d', '#fb7185', '#a78bfa',
  '#2dd4bf', '#38bdf8', '#fde047', '#f472b6', '#818cf8',
];

export class RaceData {
  constructor() {
    this.teams = [];
    this.raceUrl = '';
    this.lastUpdate = null;
    this.pollTimer = null;
    this.callbacks = new Set();
    this.timeRange = { start: null, end: null };
  }

  /**
   * Subscribe to data updates
   */
  subscribe(callback) {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback);
  }

  /**
   * Notify all subscribers
   */
  notify() {
    this.callbacks.forEach(cb => cb(this.teams, this.timeRange));
  }

  /**
   * Fetch race data - tries binary format first, falls back to JSON API
   */
  async fetch() {
    try {
      // Try binary format first (much faster)
      const binaryData = await this.fetchBinary();
      if (binaryData) {
        this.teams = binaryData.teams;
        this.timeRange = binaryData.timeRange;
        this.lastUpdate = new Date();
        this.notify();
        console.log(`Loaded ${this.teams.length} teams from binary format`);
        return this.teams;
      }
    } catch (error) {
      console.warn('Binary format not available, falling back to API:', error.message);
    }

    // Fall back to JSON API
    return this.fetchFromAPI();
  }

  /**
   * Fetch binary race data
   */
  async fetchBinary() {
    const startTime = performance.now();
    const data = await fetchBinaryRaceData(BINARY_URL);
    const elapsed = (performance.now() - startTime).toFixed(1);
    console.log(`Binary decode: ${elapsed}ms`);
    return data;
  }

  /**
   * Fetch from JSON API (fallback)
   */
  async fetchFromAPI() {
    try {
      const startTime = performance.now();
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.raceUrl = data.raceUrl || '';
      this.teams = this.transformTeams(data.teams || []);
      this.calculateTimeRange();
      this.lastUpdate = new Date();

      const elapsed = (performance.now() - startTime).toFixed(1);
      console.log(`API fetch + parse: ${elapsed}ms`);

      this.notify();
      return this.teams;
    } catch (error) {
      console.error('Failed to fetch race data:', error);
      throw error;
    }
  }

  /**
   * Transform raw API data into normalized team objects
   */
  transformTeams(rawTeams) {
    return rawTeams
      .map((team, index) => {
        const positions = (team.positions || [])
          .map(pos => ({
            lat: pos.latitude,
            lng: pos.longitude,
            speed: pos.sogKnots || 0,
            speedKmh: pos.sogKmph || 0,
            dtf: pos.dtfNm || 0,
            dtfKm: pos.dtfKm || 0,
            cog: pos.cog || 0,
            battery: pos.battery,
            timestamp: new Date(pos.gpsAt),
            id: pos.id,
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        const latestPos = positions[positions.length - 1];

        return {
          serial: team.serial,
          marker: team.marker,
          name: team.name || `Team ${team.serial}`,
          color: TEAM_COLORS[index % TEAM_COLORS.length],
          positions,
          latest: latestPos,
          stats: this.calculateTeamStats(positions),
        };
      })
      .filter(team => team.positions.length > 0)
      .sort((a, b) => {
        const aDtf = a.latest?.dtf ?? Infinity;
        const bDtf = b.latest?.dtf ?? Infinity;

        // Both finished: sort by finish time (earlier = better)
        if (aDtf === 0 && bDtf === 0) {
          const aTime = a.latest?.timestamp?.getTime() ?? 0;
          const bTime = b.latest?.timestamp?.getTime() ?? 0;
          return aTime - bTime;
        }

        // One finished, one racing: finished comes first
        if (aDtf === 0) return -1;
        if (bDtf === 0) return 1;

        // Both racing: sort by DTF
        return aDtf - bDtf;
      });
  }

  /**
   * Calculate derived statistics for a team
   */
  calculateTeamStats(positions) {
    if (positions.length === 0) return {};

    const speeds = positions.map(p => p.speed).filter(s => s > 0);
    const avgSpeed = speeds.length > 0
      ? speeds.reduce((a, b) => a + b, 0) / speeds.length
      : 0;

    const maxSpeed = Math.max(...speeds, 0);

    // Calculate total distance traveled (approximate via positions)
    let totalDistance = 0;
    for (let i = 1; i < positions.length; i++) {
      totalDistance += this.haversineDistance(
        positions[i - 1].lat, positions[i - 1].lng,
        positions[i].lat, positions[i].lng
      );
    }

    return {
      avgSpeed: avgSpeed.toFixed(1),
      maxSpeed: maxSpeed.toFixed(1),
      totalDistance: totalDistance.toFixed(1),
      positionCount: positions.length,
    };
  }

  /**
   * Calculate time range of the race
   */
  calculateTimeRange() {
    let minTime = Infinity;
    let maxTime = -Infinity;

    this.teams.forEach(team => {
      team.positions.forEach(pos => {
        const time = pos.timestamp.getTime();
        if (time < minTime) minTime = time;
        if (time > maxTime) maxTime = time;
      });
    });

    this.timeRange = {
      start: new Date(minTime),
      end: new Date(maxTime),
    };
  }

  /**
   * Get team positions at a specific timestamp
   */
  getPositionsAtTime(targetTime) {
    return this.teams.map(team => {
      const positions = team.positions;

      // Find positions bracketing the target time
      let before = null;
      let after = null;

      for (let i = 0; i < positions.length; i++) {
        const pos = positions[i];
        if (pos.timestamp <= targetTime) {
          before = pos;
        }
        if (pos.timestamp >= targetTime && !after) {
          after = pos;
          break;
        }
      }

      // If no position data at this time, return null
      if (!before && !after) return { ...team, interpolated: null, trailPositions: [] };

      // Get historical positions up to target time
      const historicalPositions = positions.filter(p => p.timestamp <= targetTime);

      // If only after exists (team hasn't started yet), use first position
      if (!before) {
        return {
          ...team,
          interpolated: after,
          trailPositions: [after],
        };
      }

      // If only before exists (past last position), use last known position
      if (!after) {
        return {
          ...team,
          interpolated: before,
          trailPositions: historicalPositions,
        };
      }

      // Interpolate between positions
      const t = (targetTime - before.timestamp) / (after.timestamp - before.timestamp);

      const interpolated = {
        lat: before.lat + (after.lat - before.lat) * t,
        lng: before.lng + (after.lng - before.lng) * t,
        speed: before.speed + (after.speed - before.speed) * t,
        dtf: before.dtf + (after.dtf - before.dtf) * t,
        cog: this.interpolateAngle(before.cog, after.cog, t),
        battery: before.battery,
        timestamp: targetTime,
      };

      return {
        ...team,
        interpolated,
        trailPositions: [...historicalPositions, interpolated],
      };
    }).filter(t => t.interpolated);
  }

  /**
   * Interpolate between two angles (handles wrap-around)
   */
  interpolateAngle(a, b, t) {
    const diff = ((b - a + 540) % 360) - 180;
    return (a + diff * t + 360) % 360;
  }

  /**
   * Haversine distance calculation (in nautical miles)
   */
  haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 3440.065; // Earth radius in nautical miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  toRad(deg) {
    return deg * Math.PI / 180;
  }

  /**
   * Start polling for live updates
   */
  startPolling() {
    if (this.pollTimer) return;

    this.pollTimer = setInterval(() => {
      this.fetch().catch(console.error);
    }, POLL_INTERVAL);

    console.log(`📡 Live polling started (${POLL_INTERVAL / 1000}s interval)`);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      console.log('📡 Live polling stopped');
    }
  }

  /**
   * Get team by serial number
   */
  getTeam(serial) {
    return this.teams.find(t => t.serial === serial);
  }

  /**
   * Get current leader
   */
  getLeader() {
    return this.teams[0] || null;
  }
}

// Singleton instance
export const raceData = new RaceData();
